import type { Node } from "@xyflow/react";

export type WorkflowStatus = "draft" | "active" | "inactive";

export type ScopeType =
  "company" | "department" | "branch" | "team" | "project";

export interface ScopeOption {
  id: number;
  name: string;
  type: ScopeType;
}

export type ScopeOptionsByType = Record<ScopeType, ScopeOption[]>;

export type Tool = "ai" | "setup" | "sort";

export type EditorMode = "edit" | "view";

// Approver routing strategy on a workflow node. Mirrors the backend enum.
// SPECIFIC_USER → uses approverId (picker scoped to workflow's branch).
// Designation types are valid only when matching the workflow's scope.
export type ApproverType =
  | "specific_user"
  | "branch_manager"
  | "department_head"
  | "project_manager"
  | "team_lead";

export const APPROVER_TYPE = {
  SPECIFIC_USER: "specific_user",
  BRANCH_MANAGER: "branch_manager",
  DEPARTMENT_HEAD: "department_head",
  PROJECT_MANAGER: "project_manager",
  TEAM_LEAD: "team_lead",
} as const satisfies Record<string, ApproverType>;

// Maps workflow scope type → the single designation that is valid for a
// node in that scope. Builder uses this to limit the dropdown options.
// Maps scope → primary designation (used as default when switching to By Role).
// project allows BOTH project_manager and team_lead (project has teams);
// the full allowed list is in ROLE_APPROVER_TYPES — sidebar shows all of them.
export const SCOPE_TO_DESIGNATION: Record<ScopeType, ApproverType | null> = {
  company: null,
  branch: APPROVER_TYPE.BRANCH_MANAGER,
  department: APPROVER_TYPE.DEPARTMENT_HEAD,
  team: APPROVER_TYPE.TEAM_LEAD,
  project: APPROVER_TYPE.PROJECT_MANAGER,
};

export const APPROVER_TYPE_LABEL: Record<ApproverType, string> = {
  specific_user: "Specific Person",
  branch_manager: "Branch Manager",
  department_head: "Department Head",
  project_manager: "Project Manager",
  team_lead: "Team Lead",
};

export interface Workflow {
  id?: string;
  name: string;
  description: string;
  module: ModuleType;
  status: string;
  scopeType: string;
  scopeIds: number[];
  scopeNames: string[];
  // Branch this workflow is anchored to. Required server-side; the
  // backend defaults to the creator's branch when unset.
  branchId?: number | null;
  branch?: { id: number; name: string } | null;
  // True when the live edit state is ahead of the latest published
  // version — the user did "Save as draft" but hasn't published yet.
  // Triggers the "Unpublished changes" chip in the workflows list.
  hasUnpublishedChanges?: boolean;
  instanceCounts?: {
    active: number;
    completed: number;
    total: number;
  };
  versionCount?: number;
  eventTypeCode?: string;
  condition: WorkflowConditionPayload;
  nodes: {
    id: string;
    stepOrder: number;
    nodeName: string;
    approverId: string;
    nodeDescription: string;
    approverType: ApproverType;
    timeoutHours?: number;
    escalationNodeId?: number | null;
    // Optional display labels populated by the detail endpoint so read-only
    // consumers can render names instead of raw ids.
    approverName?: string | null;
    approverEmail?: string | null;
  }[];
  // Mandatory fallback approver — receives the request whenever a node's
  // designated approver cannot be resolved at runtime.
  fallbackUserId?: number | null;
  createdAt: string;
  // Latest published version metadata. Surfaced in the builder header so the
  // user can tell which version live instances are pinned to.
  latestVersionNumber?: number | null;
  lastPublishedAt?: string | null;
  // Set by the backend when the builder is loading a frozen version snapshot
  // (e.g. opened from the Workflow Instances eye action). Triggers read-only
  // header — no save/publish/unpublish.
  isVersionSnapshot?: boolean;
  versionId?: number | null;
}

export interface WorkflowEvent {
  id: number;
  eventCode: string;
  eventName: string;
  description: string;
}

export interface WorkflowEventTypeGroup {
  module: ModuleType;
  events: WorkflowEvent[];
}

export type LeaveConditionPayload = {
  leave: {
    leaveTypeId: number;
    leaveTypeName?: string | null;
    leaveSubtypeId?: number | null;
    leaveSubtypeName?: string | null;
  };
};
export type AssetConditionPayload = { asset: { assetCategory: string } };
export type OvertimeConditionPayload = {
  overtime: {
    type?: "PRE_APPROVED" | "POST_FACTO" | "AUTO_DETECTED" | null;
    isActive?: boolean;
  };
};
export type WorkflowConditionPayload =
  | LeaveConditionPayload
  | AssetConditionPayload
  | OvertimeConditionPayload
  | undefined;

export type ModuleType =
  "leave_management" | "asset_management" | "offboarding" | "overtime_requests";

export type LeaveConditionData = {
  module: "leave_management";
  condition?: LeaveConditionPayload;
};
export type AssetConditionData = {
  module: "asset_management";
  condition?: AssetConditionPayload;
};
export type OvertimeConditionData = {
  module: "overtime_requests";
  condition?: OvertimeConditionPayload;
};

export type TriggerCondition =
  | LeaveConditionData["condition"]
  | AssetConditionData["condition"]
  | OvertimeConditionData["condition"];

export type StepNodeData = {
  stepOrder?: number;
  nodeName?: string;
  approverType?: ApproverType;
  approverId?: string;
  nodeDescription?: string;
  timeoutHours?: number;
  escalationNodeId?: string | number | null;
  escalationNodeName?: string;
} & NodeActions;

export type TriggerNodeData = (
  LeaveConditionData | AssetConditionData | OvertimeConditionData
) & {
  scopeType?: ScopeType;
  scopeIds?: number[];
  branchId?: number | null;
  eventTypeCode?: string;
  fallbackUserId?: number | null;
  onEventTypeChange?: (eventTypeCode: string) => void;
} & NodeActions;

type NodeActions = {
  onAddNode?: () => void;
  onEditNode?: () => void;
  onEditNodeScope?: () => void;
  onDeleteNode?: () => void;
  onValuesChange?: (values: Record<string, any>) => void;
  isSelected?: boolean;
};

export type ChecksNodeData = {
  module?: ModuleType;
  leaveTypeId?: number;
} & NodeActions;

export type ITriggerNode = Node<TriggerNodeData, "trigger">;
export type IStepNode = Node<StepNodeData, "step">;
export type IChecksNode = Node<ChecksNodeData, "checks">;

export type WorkflowNode = ITriggerNode | IStepNode | IChecksNode;

export const isTriggerNode = (
  node: WorkflowNode | Node | undefined,
): node is ITriggerNode => node?.type === "trigger";

export const isStepNode = (
  node: WorkflowNode | Node | undefined,
): node is IStepNode => node?.type === "step";

export const isChecksNode = (
  node: WorkflowNode | Node | undefined,
): node is IChecksNode => node?.type === "checks";

export enum ENUM_MODULE_TYPE {
  LEAVE_MANAGEMENT = "leave_management",
  ASSET_MANAGEMENT = "asset_management",
  OVERTIME_REQUESTS = "overtime_requests",
}
